// ---------------------------------------------------------------------------
// Rede de proteção dos dados da ficha.
//
// A ficha é montada a partir do banco + do rascunho guardado no aparelho.
// Se qualquer coisa vier torta (pergunta apagada, condição apontando para uma
// pergunta que não existe mais, rascunho antigo com outro formato), a tela
// NUNCA pode quebrar: a gente ignora o que está errado e segue com o resto.
// ---------------------------------------------------------------------------
import type { PerguntaFicha, ValorResposta } from './types';
import { normalizarCondicaoValor, normalizarSenha } from './utils';

const TIPOS_VALIDOS = [
  'texto_curto',
  'texto_longo',
  'sim_nao',
  'escolha_unica',
  'multipla_escolha',
  'numero',
  'data',
  'telefone',
  'email',
  'cpf',
  'lista',
  'foto',
];

export interface PerguntaBruta {
  id: number | string | null;
  ordem: number | string | null;
  secao: string | null;
  chave: string | null;
  enunciado: string | null;
  descricao: string | null;
  tipo: string | null;
  obrigatoria: boolean | null;
  opcoes: unknown;
  condicao_pergunta_id: number | string | null;
  condicao_valor: unknown;
}

// "Família" e "Família:" são a mesma seção — sem isso a pergunta condicional
// caía num bloco sozinho (e um bloco escondido deixa a tela em branco).
export function nomeDaSecao(secao: string | null | undefined): string | null {
  if (secao == null) return null;
  const limpo = String(secao).trim().replace(/[:：\s]+$/, '');
  return limpo === '' ? null : limpo;
}

export function chaveDaSecao(secao: string | null | undefined): string | null {
  const nome = nomeDaSecao(secao);
  return nome == null ? null : nome.toLowerCase();
}

function textoLimpo(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : v;
}

// Monta a lista de perguntas descartando o que não dá para renderizar e
// ignorando condições que apontam para perguntas que não existem mais.
export function perguntasSeguras(linhas: unknown): PerguntaFicha[] {
  const cruas = (Array.isArray(linhas) ? linhas : []) as PerguntaBruta[];
  const lista: PerguntaFicha[] = [];
  const condicoes = new Map<number, number | null>();

  cruas.forEach((l) => {
    if (!l || typeof l !== 'object') return;

    const id = Number(l.id);
    if (!Number.isFinite(id)) return;

    const tipo = String(l.tipo ?? '');
    if (!TIPOS_VALIDOS.includes(tipo)) return;

    const enunciado = typeof l.enunciado === 'string' ? l.enunciado : '';
    if (enunciado.trim() === '') return;

    const ordem = Number(l.ordem);
    const condId = Number(l.condicao_pergunta_id);
    condicoes.set(id, Number.isFinite(condId) ? condId : null);

    lista.push({
      id,
      ordem: Number.isFinite(ordem) ? ordem : id,
      secao: nomeDaSecao(l.secao),
      chave: typeof l.chave === 'string' && l.chave.trim() !== '' ? l.chave : null,
      enunciado,
      descricao: typeof l.descricao === 'string' ? textoLimpo(l.descricao) : null,
      tipo: tipo as PerguntaFicha['tipo'],
      obrigatoria: l.obrigatoria === true,
      opcoes: Array.isArray(l.opcoes)
        ? l.opcoes
            .filter((o) => o != null)
            .map((o) => String(o))
            .filter((o) => o.trim() !== '')
        : [],
      condicaoPerguntaId: null,
      condicaoValor: normalizarCondicaoValor(l.condicao_valor),
    });
  });

  const existentes = new Set(lista.map((p) => p.id));
  lista.forEach((p) => {
    // Sem valor de comparação não existe condição — a pergunta aparece.
    if (p.condicaoValor == null) return;

    const condId = condicoes.get(p.id) ?? null;
    if (condId == null) return;
    // Condição órfã: a pergunta aparece (melhor aparecer do que sumir para sempre).
    if (!existentes.has(condId) || condId === p.id) return;
    p.condicaoPerguntaId = condId;
  });

  return lista;
}

// Rascunho: mantém só as respostas de perguntas que ainda existem e no formato
// que o campo espera. Qualquer coisa estranha é simplesmente descartada.
export function rascunhoSeguro(
  dados: unknown,
  perguntas: PerguntaFicha[],
): Record<string, ValorResposta> {
  const saida: Record<string, ValorResposta> = {};
  if (!dados || typeof dados !== 'object' || Array.isArray(dados)) return saida;

  const porId = new Map(perguntas.map((p) => [String(p.id), p]));

  Object.entries(dados as Record<string, unknown>).forEach(([chave, valor]) => {
    const p = porId.get(chave);
    if (!p || valor == null) return;

    if (p.tipo === 'multipla_escolha') {
      if (!Array.isArray(valor)) return;
      const opcoes = valor
        .filter((v) => typeof v === 'string' || typeof v === 'number')
        .map((v) => String(v))
        .filter((v) => v !== '');
      if (opcoes.length > 0) saida[chave] = opcoes;
      return;
    }

    if (p.tipo === 'lista') {
      if (!Array.isArray(valor)) return;
      const linhas = valor
        .filter((l) => l != null && typeof l === 'object' && !Array.isArray(l))
        .map((l) => {
          const linha: Record<string, string> = {};
          Object.entries(l as Record<string, unknown>).forEach(([coluna, v]) => {
            if (typeof v === 'string' || typeof v === 'number') linha[coluna] = String(v);
          });
          return linha;
        })
        .filter((l) => Object.keys(l).length > 0);
      if (linhas.length > 0) saida[chave] = linhas;
      return;
    }

    if (p.tipo === 'sim_nao') {
      if (valor === true || valor === 'true') saida[chave] = 'true';
      else if (valor === false || valor === 'false') saida[chave] = 'false';
      return;
    }

    if (typeof valor !== 'string' && typeof valor !== 'number') return;
    let texto = String(valor);
    if (texto.trim() === '') return;
    if (p.chave === 'senha_fila') texto = normalizarSenha(texto);
    if (texto.trim() === '') return;
    saida[chave] = texto;
  });

  return saida;
}